import { acceptHMRUpdate, defineStore } from "pinia";
import type DirectWork from "../../../backend/app/models/direct_work";
import type Observation from "../../../backend/app/models/observation";
import type { Report } from "~/types/report";

interface ReportData {
  report: Report;
  observations: Observation[];
  directWorks: DirectWork[];
}

interface UpdateReportPayload {
  status?: "process" | "completed" | "created" | "generated";
  title?: string;
  obsIds?: number[];
  dwIds?: number[];
  textBlocks?: any[];
  pdfLink?: string;
  generatePdf?: boolean; // Optional flag to trigger PDF generation
}

export const useReportsStore = defineStore("reports", () => {
  const { $api } = useNuxtApp();
  const authStore = useAuthStore();

  // State
  const reports: Ref<Report[]> = ref([]);
  const currentReport: Ref<Report | null> = ref(null);
  const currentReportData: Ref<ReportData | null> = ref(null);
  const isLoading = ref(false);
  const error: Ref<string | null> = ref(null);
  const needRefresh = ref(false);

  // Pagination
  const currentPage = ref(1);
  const totalPages = ref(1);
  const perPage = ref(50);

  /**
   * Fetch all reports with filters
   */
  async function fetchReports(
    filters: {
      status?: "process" | "completed" | "created" | "generated";
      residentId?: number;
      page?: number;
      limit?: number;
    } = {},
  ) {
    isLoading.value = true;
    error.value = null;

    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.residentId) queryParams.append("residentId", filters.residentId.toString());
      if (filters.page) queryParams.append("page", filters.page.toString());
      if (filters.limit) queryParams.append("limit", filters.limit.toString());

      const response = await $fetch<any>(`${$api("/reports")}?${queryParams.toString()}`, {
        method: "GET",
        credentials: "include",
      });
      reports.value = response.data;
      currentPage.value = response.meta.current_page;
      totalPages.value = response.meta.last_page;
      perPage.value = response.meta.per_page;

      return response;
    } catch (err: any) {
      error.value = err.message || "Failed to fetch reports";
      console.error("Error fetching reports:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch a single report by ID
   */
  async function fetchReport(reportId: number) {
    isLoading.value = true;
    error.value = null;

    try {
      const report = await $fetch<Report>(`${$api("/reports")}/${reportId}`, {
        method: "GET",
        credentials: "include",
      });

      currentReport.value = report;
      return report;
    } catch (err: any) {
      error.value = err.message || "Failed to fetch report";
      console.error("Error fetching report:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch report preview data (using token)
   */
  async function fetchReportPreview(reportId: number, token: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const data = await authStore.api<ReportData>(
        "get",
        `/reports/${reportId}/preview?token=${token}`,
      );

      currentReportData.value = data;
      return data;
    } catch (err: any) {
      error.value = err.message || "Failed to fetch report preview";
      console.error("Error fetching report preview:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Create a new report
   */
  async function createReport(payload: Partial<Report>) {
    isLoading.value = true;
    error.value = null;

    try {
      const report = await $fetch<Report>(`${$api("/reports")}`, {
        method: "POST",
        credentials: "include",
        body: payload,
      });

      // Add to reports list
      reports.value.unshift(report);
      currentReport.value = report;
      markForRefresh();
      return report;
    } catch (err: any) {
      error.value = err.message || "Failed to create report";
      console.error("Error creating report:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Update a report
   * @param reportId - The ID of the report to update
   * @param payload - The update payload
   * @param options - Optional configuration
   * @param options.generatePdf - Whether to trigger PDF generation (default: false)
   */
  async function updateReport(
    reportId: number,
    payload: UpdateReportPayload,
    options?: { generatePdf?: boolean },
  ) {
    isLoading.value = true;
    error.value = null;

    try {
      // Include generate_pdf flag if explicitly requested
      const body = {
        ...payload,
        ...(options?.generatePdf !== undefined && { generate_pdf: options.generatePdf }),
      };

      const report = await $fetch<Report>(`${$api("/reports")}/${reportId}`, {
        method: "PATCH",
        credentials: "include",
        body,
      });

      // Update in reports list
      const index = reports.value.findIndex((r) => r.id === reportId);
      if (index !== -1) {
        reports.value[index] = report;
      }
      markForRefresh();
      // Update current report if it's the same one
      if (currentReport.value?.id === reportId) {
        currentReport.value = report;
      }

      return report;
    } catch (err: any) {
      error.value = err.message || "Failed to update report";
      console.error("Error updating report:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Delete a report
   */
  async function deleteReport(reportId: number) {
    isLoading.value = true;
    error.value = null;

    try {
      await $fetch(`${$api("/reports")}/${reportId}`, {
        method: "DELETE",
        credentials: "include",
      });

      // Remove from reports list
      reports.value = reports.value.filter((r) => r.id !== reportId);
      markForRefresh();
      // Clear current report if it's the deleted one
      if (currentReport.value?.id === reportId) {
        currentReport.value = null;
      }

      return true;
    } catch (err: any) {
      error.value = err.message || "Failed to delete report";
      console.error("Error deleting report:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Duplicate a report — creates a draft copy with " (copy)" appended to the
   * title (no PDF generation). The copy is prepended to the list, matching
   * the backend's created_at desc ordering.
   */
  async function duplicateReport(reportId: number) {
    isLoading.value = true;
    error.value = null;

    try {
      const report = await $fetch<Report>(`${$api("/reports")}/${reportId}/duplicate`, {
        method: "POST",
        credentials: "include",
      });

      reports.value.unshift(report);
      markForRefresh();
      return report;
    } catch (err: any) {
      error.value = err.message || "Failed to duplicate report";
      console.error("Error duplicating report:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Generate PDF for a report (dispatches background job)
   */
  async function generatePdf(reportId: number) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await $fetch<{ message: string; report: Report }>(
        `${$api("/reports")}/${reportId}/generate`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      // Update the report in the list
      const index = reports.value.findIndex((r) => r.id === reportId);
      if (index !== -1) {
        reports.value[index] = response.report;
      }

      // Update current report
      if (currentReport.value?.id === reportId) {
        currentReport.value = response.report;
      }
      markForRefresh();
      return response;
    } catch (err: any) {
      error.value = err.message || "Failed to generate PDF";
      console.error("Error generating PDF:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Get preview URL for a report
   */
  function getPreviewUrl(report: Report): string {
    const config = useRuntimeConfig();
    const baseUrl = config.public.frontendUrl || window.location.origin;
    return `${baseUrl}/reports/preview/${report.id}?token=${report.view_token}`;
  }

  /**
   * Clear current report data
   */
  function clearCurrentReport() {
    currentReport.value = null;
    currentReportData.value = null;
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null;
  }

  /**
   * Mark that reports need to be refreshed
   */
  function markForRefresh() {
    needRefresh.value = true;
  }

  /**
   * Clear the refresh flag
   */
  function clearRefreshFlag() {
    needRefresh.value = false;
  }

  /**
   * Get reports by status
   */
  const reportsByStatus = computed(() => {
    return (status: "process" | "completed") => {
      return reports.value.filter((r) => r.status === status);
    };
  });

  /**
   * Get reports by resident
   */
  const reportsByResident = computed(() => {
    return (residentId: number) => {
      return reports.value.filter((r) => r.resident_id === residentId);
    };
  });

  /**
   * Check if report is being processed
   */
  /**
   * Check if report is being processed
   */
  const isReportProcessing = computed(() => {
    return (reportId: number) => {
      const report = reports.value.find((r) => r.id === reportId);
      return report?.status === "process" && !report?.pdf_url;
    };
  });

  /**
   * Check if report is completed
   */
  const isReportCompleted = computed(() => {
    return (reportId: number) => {
      const report = reports.value.find((r) => r.id === reportId);
      return report?.status === "completed" && !!report?.pdf_url;
    };
  });

  return {
    // State
    reports,
    currentReport,
    currentReportData,
    isLoading,
    error,
    currentPage,
    totalPages,
    perPage,
    needRefresh,

    // Actions
    fetchReports,
    fetchReport,
    fetchReportPreview,
    createReport,
    updateReport,
    deleteReport,
    duplicateReport,
    generatePdf,
    getPreviewUrl,
    clearCurrentReport,
    clearError,
    markForRefresh,
    clearRefreshFlag,

    // Computed
    reportsByStatus,
    reportsByResident,
    isReportProcessing,
    isReportCompleted,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReportsStore, import.meta.hot));
}
